import React, { FunctionComponent, useState, useEffect } from "react";
import { useSelector } from "react-redux";
import { useI18n } from "../../../hooks/useI18n";
import { AppState } from "../../../store";
import { ReportedStocktaking } from "../../../models/StoreInventory";
import MaterialTable, { MTableToolbar } from "@material-table/core";
import moment from "moment";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogContent,
  DialogContentText,
  IconButton,
  LinearProgress,
  Paper,
  Snackbar,
  TextField,
  Typography
} from "@material-ui/core";
import { Alert, AlertTitle } from "@material-ui/lab";
import { ConfirmDialog } from "../../../components/dialogs/ConfirmDialog";
import * as InventoryApi from "../../../api/storeInventoryApi";
import { isOk } from "../../../result";
import MuiDialogTitle from "@material-ui/core/DialogTitle";
import CloseIcon from "@material-ui/icons/Close";
import useStyles from "./styles";
import * as StocktakingApi from "../../../api/stocktakingApi";
import { ProductMinimalModel } from "../../../models/Product";
import { StoreStocktakingId } from "../../../models/Stocktaking";

interface ProductStocktakingProps {
  inventoryItem: any;
  storeId: string;
  selectedStocktakingId?: string;
  onSave: (reload: boolean) => void;
}

export const ProductStocktaking: FunctionComponent<ProductStocktakingProps> = ({
  inventoryItem,
  storeId,
  selectedStocktakingId,
  onSave
}: any) => {
  const classes = useStyles();
  const { t } = useI18n();

  const [state, setState] = useState({
    stocktakings: [] as ReportedStocktaking[],
    stocktakingId: null as any,
    stocktakingOnShelf: 0,
    previousStocktakingOnShelf: 0,
    showConfirmRemoveStocktaking: false,
    showUpdateStocktaking: false,
    showAddStocktaking: false,
    successBody: null as any,
    errors: {} as any,
    problem: null as any,
    loading: false
  });

  useEffect(() => {
    if (
      inventoryItem &&
      inventoryItem.reportedStocktaking &&
      inventoryItem.reportedStocktaking.length > 0
    ) {
      //This should never happen
      if (inventoryItem.reportedProductDelivery.length === 0) {
        console.error("reportedProductDelivery is empty");
        return setState({
          ...state,
          stocktakings: [],
          stocktakingId: null
        });
      }

      //This is because create is when stocktaking is initiated and update changes when you edit a stocktaking (delivery create never changes)
      const stocktakingWithDeliveryCreate =
        inventoryItem.reportedStocktaking.reduce((acc: any, curr: any) => {
          let matchingDelivery = inventoryItem.reportedProductDelivery.find(
            (x: any) => x.storeStocktakingId === curr.id
          );

          if (matchingDelivery) {
            curr.created = matchingDelivery.created;
          }
          acc.push(curr);
          return acc;
        }, []);

      inventoryItem.reportedProductDelivery.sort(
        (a: any, b: any) => a.created - b.created
      );
      //Use moment since formats can be different because of delivery create
      stocktakingWithDeliveryCreate.sort((a: any, b: any) =>
        moment(b.created).diff(a.created)
      );

      const stocktakingId =
        stocktakingWithDeliveryCreate[0].created >
          inventoryItem.reportedProductDelivery[0].created ||
        stocktakingWithDeliveryCreate[0].id ===
          inventoryItem.reportedProductDelivery[0].storeStocktakingId
          ? stocktakingWithDeliveryCreate[0].id
          : null;

      setState({
        ...state,
        stocktakings: stocktakingWithDeliveryCreate,
        stocktakingId: stocktakingId
      });
    }
  }, [inventoryItem]);

  const removeReportedProductStocktaking = async (id: string) => {
    const result = await InventoryApi.removeReportedProductStocktaking(id);

    if (isOk(result)) {
      const stocktakings = state.stocktakings.filter(
        stockTaking => stockTaking.id !== id
      );

      setState({
        ...state,
        stocktakings: stocktakings,
        showConfirmRemoveStocktaking: false,
        successBody: "The product stocktaking has been removed"
      });

      return onSave(true);
    }

    return setState({
      ...state,
      showConfirmRemoveStocktaking: false,
      successBody: null,
      problem: result.details
    });
  };

  const calculateOnShelfAfterChange = () => {
    const index = state.stocktakings.findIndex(
      (x: any) => x.id === state.stocktakingId
    );
    if (index === 0) {
      return (
        inventoryItem.onShelf -
        state.previousStocktakingOnShelf +
        (state.stocktakingOnShelf ? state.stocktakingOnShelf : 0)
      );
    }

    return inventoryItem.onShelf;
  };

  return inventoryItem ? (
    <>
      <MaterialTable
        title=""
        columns={[
          {
            title: "Created",
            field: "created",
            cellStyle: { padding: "8px" },
            render: stocktaking => {
              return moment(stocktaking.created).format("YYYY-MM-DD HH:mm");
            }
          },
          {
            title: "Updated",
            field: "updated",
            cellStyle: { padding: "8px" },
            render: stocktaking => {
              return moment(stocktaking.updated ?? stocktaking.created).format(
                "YYYY-MM-DD HH:mm"
              );
            }
          },
          {
            title: t("common.formFields.reportedBy"),
            field: "reportedBy",
            cellStyle: { padding: "8px" }
          },
          {
            title: t("common.formFields.onShelf"),
            field: "onShelf",
            cellStyle: { padding: "8px" }
          }
        ]}
        data={
          state.stocktakings
            ? (state.stocktakings as ReportedStocktaking[])
            : []
        }
        actions={[
          {
            icon: "add_circle",
            iconProps: {
              style: { color: "rgb(31, 132, 52)", fontSize: "2rem" }
            },
            tooltip: "Add new stock taking",
            isFreeAction: true,
            onClick: () => {
              return setState({
                ...state,
                showAddStocktaking: true,
                stocktakingOnShelf: 0
              });
            }
          },
          (rowData: any) => ({
            icon: "edit",
            tooltip: "Update stock taking",
            iconProps:
              rowData.id !== state.stocktakingId
                ? { style: { color: "rgba(0, 0, 0, 0.24)" } }
                : { style: { color: "rgba(0, 0, 0, 0.54)" } },
            disabled: rowData.id !== state.stocktakingId,
            onClick: (event, rowData: any) => {
              setState({
                ...state,
                showUpdateStocktaking: true,
                stocktakingOnShelf: rowData.onShelf,
                previousStocktakingOnShelf: rowData.onShelf
              });
            }
          }),
          (rowData: any) => ({
            icon: "delete",
            tooltip: t("common.actions.removeStocktaking"),
            iconProps:
              rowData.id !== state.stocktakingId
                ? { style: { color: "rgba(0, 0, 0, 0.24)" } }
                : { style: { color: "rgba(0, 0, 0, 0.54)" } },
            disabled: rowData.id !== state.stocktakingId,
            onClick: (event, rowData: any) => {
              setState({
                ...state,
                showConfirmRemoveStocktaking: true,
                stocktakingOnShelf: rowData.onShelf
              });
            }
          })
        ]}
        components={{
          Container: props => <Paper {...props} elevation={0} />
        }}
        options={{
          headerStyle: {
            padding: "8px"
          },
          actionsCellStyle: { whiteSpace: "nowrap" },
          actionsColumnIndex: -1,
          pageSize: 5,
          search: false,
          pageSizeOptions: [20, 30, 40],
          sorting: false,
          rowStyle: rowData => {
            if (rowData.id === selectedStocktakingId) {
              return {
                backgroundColor: "rgba(5,89,141, 0.1)"
              };
            }

            return {};
          }
        }}
        localization={{
          header: {
            actions: ""
          }
        }}
      />

      {state.stocktakings.length > 0 && state.stocktakingId == null && (
        <Alert severity="info" className={classes.noUpdateInfo}>
          No stock takings can be updated. Create a new stock taking to update
          on shelf value.
        </Alert>
      )}

      <Dialog
        fullWidth={true}
        maxWidth={"sm"}
        open={state.showUpdateStocktaking}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle>
          Update stock taking
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => {
              setState({
                ...state,
                showUpdateStocktaking: false
              });
            }}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent>
          <TextField
            className={classes.field}
            type="number"
            label={t("common.formFields.onShelf")}
            value={state.stocktakingOnShelf}
            error={calculateOnShelfAfterChange() < 0}
            helperText={`Currently on shelf: ${inventoryItem.onShelf}`}
            onChange={e => {
              const value = parseInt(e.target.value);
              setState({
                ...state,
                stocktakingOnShelf: value
              });
            }}
          />
          <h3>{`Total on shelf after stock taking: ${calculateOnShelfAfterChange()}`}</h3>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: 20
            }}
          >
            <Button
              style={{ width: 200 }}
              variant="contained"
              color="primary"
              disabled={calculateOnShelfAfterChange() < 0 || state.loading}
              onClick={async () => {
                setState({ ...state, loading: true });
                const result =
                  await StocktakingApi.updateReportedProductStocktaking(
                    state.stocktakingId,
                    state.stocktakingOnShelf
                  );
                if (result.type === "OK") {
                  const stocktakings = state.stocktakings.map((s: any) =>
                    s.id === state.stocktakingId
                      ? {
                          ...s,
                          onShelf: state.stocktakingOnShelf
                        }
                      : s
                  );

                  setState({
                    ...state,
                    showUpdateStocktaking: false,
                    stocktakings: stocktakings,
                    successBody: "Stocktaking updated successfully",
                    problem: null,
                    loading: false
                  });

                  onSave(true);
                } else {
                  setState({
                    ...state,
                    successBody: "",
                    problem: result.details,
                    loading: false
                  });
                }
              }}
            >
              {state.loading ? <CircularProgress size={25} /> : <>Save</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog
        fullWidth={true}
        maxWidth={"sm"}
        open={state.showAddStocktaking}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <MuiDialogTitle>
          Add new stock taking
          <IconButton
            aria-label="close"
            className={classes.closeButton}
            onClick={() => {
              setState({ ...state, showAddStocktaking: false });
            }}
          >
            <CloseIcon />
          </IconButton>
        </MuiDialogTitle>
        <DialogContent>
          <TextField
            className={classes.field}
            type="number"
            label={t("common.formFields.onShelf")}
            value={state.stocktakingOnShelf}
            helperText={`Currently on shelf: ${inventoryItem.onShelf}`}
            onChange={e => {
              const value = parseInt(e.target.value);
              setState({
                ...state,
                stocktakingOnShelf: value
              });
            }}
          />
          <h3>{`Total on shelf after stock taking: ${state.stocktakingOnShelf}`}</h3>
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              marginTop: 20
            }}
          >
            <Button
              style={{ width: 200 }}
              variant="contained"
              color="primary"
              disabled={state.loading}
              onClick={async () => {
                setState({ ...state, loading: true });
                const result = await StocktakingApi.reportByStore(storeId, [
                  {
                    id: "",
                    storeId: storeId,
                    product: inventoryItem.product,
                    onShelf: state.stocktakingOnShelf,
                    onShelfBefore: inventoryItem.onShelf,
                    created: moment().toDate()
                  }
                ]);

                if (result.type === "OK") {
                  setState({
                    ...state,
                    showAddStocktaking: false,
                    successBody: "Stocktaking added successfully",
                    problem: null,
                    loading: false
                  });

                  onSave(true);
                } else {
                  setState({
                    ...state,
                    successBody: "",
                    problem: result.details,
                    loading: false
                  });
                }
              }}
            >
              {state.loading ? <CircularProgress size={25} /> : <>Save</>}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <ConfirmDialog
        visible={state.showConfirmRemoveStocktaking}
        titleText={`Remove`}
        bodyText={"Are you sure you want to remove the selected stocktaking?"}
        confirmText={"Remove"}
        onConfirm={() => {
          if (state.stocktakingId) {
            removeReportedProductStocktaking(state.stocktakingId);
          }
          return true;
        }}
        onCancel={() => {
          setState({
            ...state,
            showConfirmRemoveStocktaking: false
          });
        }}
      ></ConfirmDialog>

      <Snackbar
        open={state.successBody != null}
        autoHideDuration={2000}
        onClose={() => setState({ ...state, successBody: null })}
      >
        <Alert
          onClose={() => setState({ ...state, successBody: null })}
          severity="success"
        >
          {state.successBody ?? ""}
        </Alert>
      </Snackbar>

      <Snackbar
        open={state.problem != null}
        autoHideDuration={6000}
        onClose={() => setState({ ...state, problem: null })}
      >
        <Alert severity="error">
          <AlertTitle>
            {state.problem?.status}, {state.problem?.title}
          </AlertTitle>
          {state.problem?.detail} <strong>{state.problem?.traceId}</strong>
        </Alert>
      </Snackbar>
    </>
  ) : (
    <></>
  );
};
